import { useMemo, useState } from 'react'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, Cell } from 'recharts'
import { SaleOrder, HoardOrder } from '../../../../sales'
import { Branch } from '../../../../manager'
import { WidgetCard } from '../widget-card/widgetCard'
import { getLast12MonthRanges, isInRange, formatAmount } from '../../../utils/dashboardUtils'
import { useOperationTotals } from '../../../hooks/useOperationTotals'
import styles from './salesChart.module.css'

const BRANCH_COLORS = ['#6d9791', '#3F6B64', '#a8c8c4', '#2a4f4a', '#8ab5b0', '#1a3330', '#c2d9d7']

type TooltipPayloadItem = {
    name: string
    value: number
    color: string
    dataKey: string
}

type CustomTooltipProps = {
    active?: boolean
    payload?: TooltipPayloadItem[]
    label?: string
    showTotal: boolean
    branchNames: Record<string, string>
}

const CustomTooltip = ({ active, payload, label, showTotal, branchNames }: CustomTooltipProps) => {
    if (!active || !payload || payload.length === 0) return null

    const total = payload.reduce((sum, p) => sum + (p.value || 0), 0)

    return (
        <div className={styles.tooltip}>
            <p className={styles.tooltipLabel}>{label}</p>
            {payload.map((p, idx) => (
                <p key={idx} className={styles.tooltipRow}>
                    <span className={styles.tooltipDot} style={{ background: p.color }} />
                    <span>{branchNames[p.dataKey] || p.name}</span>
                    <span className={styles.tooltipValue}>{formatAmount(p.value)}</span>
                </p>
            ))}
            {showTotal && payload.length > 1 && (
                <p className={styles.tooltipTotal}>
                    <span>Total</span>
                    <span>{formatAmount(total)}</span>
                </p>
            )}
        </div>
    )
}

type Props = {
    loading: boolean
    error: boolean
    sales: SaleOrder[]
    hoards: HoardOrder[]
    branches: Branch[]
    isCurrentUser?: boolean
    sellerId?: string
}

export const SalesChart = (props: Props) => {
    const { loading, error, sales, hoards, branches, isCurrentUser, sellerId } = props

    const [selectedBranchId, setSelectedBranchId] = useState<string>('all')

    const { getSaleTotal, getHoardTotal } = useOperationTotals()

    const isSimple = isCurrentUser || !!sellerId

    const { chartData, branchIds } = useMemo(() => {
        const ranges = getLast12MonthRanges()

        const filteredSales = sellerId ? sales.filter(s => s.sellerId === sellerId) : sales
        const filteredHoards = sellerId ? hoards.filter(h => h.sellerId === sellerId) : hoards

        const activeBranchIds = selectedBranchId === 'all'
            ? branches.map(b => b.id)
            : [selectedBranchId]

        const data = ranges.map((range, idx) => {
            const isCurrentMonth = idx === ranges.length - 1
            const entry: Record<string, number | string | boolean> = { mes: range.label, isCurrent: isCurrentMonth }

            if (isSimple) {
                entry['ventas'] = filteredSales
                    .filter(s => isInRange(s.creationDate, range))
                    .reduce((sum, s) => sum + getSaleTotal(s), 0)
                entry['acopios'] = filteredHoards
                    .filter(h => isInRange(h.creationDate, range))
                    .reduce((sum, h) => sum + getHoardTotal(h), 0)
            } else if (selectedBranchId !== 'all') {
                entry['total'] = filteredSales
                    .filter(s => s.branchId === selectedBranchId && isInRange(s.creationDate, range))
                    .reduce((sum, s) => sum + getSaleTotal(s), 0)
                    + filteredHoards
                        .filter(h => h.branchId === selectedBranchId && isInRange(h.creationDate, range))
                        .reduce((sum, h) => sum + getHoardTotal(h), 0)
            } else {
                activeBranchIds.forEach(branchId => {
                    entry[branchId] = filteredSales
                        .filter(s => s.branchId === branchId && isInRange(s.creationDate, range))
                        .reduce((sum, s) => sum + getSaleTotal(s), 0)
                        + filteredHoards
                            .filter(h => h.branchId === branchId && isInRange(h.creationDate, range))
                            .reduce((sum, h) => sum + getHoardTotal(h), 0)
                })
            }
            return entry
        })

        return { chartData: data, branchIds: activeBranchIds }
    }, [sales, hoards, branches, sellerId, selectedBranchId])

    const formatYAxis = (value: number) =>
        value >= 1000000 ? `$${(value / 1000000).toFixed(1)}M`
        : value >= 1000 ? `$${(value / 1000).toFixed(0)}K`
        : `$${value}`

    const showStacked = !isSimple && selectedBranchId === 'all'
    const showSingleBranch = !isSimple && selectedBranchId !== 'all'
    const selectedBranchColor = showSingleBranch
        ? BRANCH_COLORS[branches.findIndex(b => b.id === selectedBranchId) % BRANCH_COLORS.length]
        : '#6d9791'

    const branchNames: Record<string, string> = {}
    branches.forEach(b => { branchNames[b.id] = b.name })

    return (
                <WidgetCard
                    title="Evolución de ventas — últimos 12 meses"
                    loading={loading}
                    error={error}
                    headerActions={!isSimple && branches.length > 1 && (
                        <select
                            className="form-select form-select-sm w-auto"
                            value={selectedBranchId}
                            onChange={e => setSelectedBranchId(e.target.value)}
                        >
                            <option value="all">Todas las sucursales</option>
                            {branches.map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                    )}
                >
                    <div className={styles.chartContainer}>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#798eb3' }} />
                                <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: '#798eb3' }} width={64} />
                                <Tooltip content={
                                    <CustomTooltip
                                        showTotal={showStacked || isSimple}
                                        branchNames={branchNames}
                                    />
                                } />
                                {(showStacked || isSimple) && <Legend />}
                                {isSimple ? (
                                    <>
                                        <Bar dataKey="ventas" name="Mis Ventas" stackId="a" fill="#6d9791" radius={[0, 0, 0, 0]} />
                                        <Bar dataKey="acopios" name="Mis Acopios" stackId="a" fill="#3F6B64" radius={[4, 4, 0, 0]} />
                                    </>
                                ) : showSingleBranch ? (
                                    <Bar dataKey="total" name={branches.find(b => b.id === selectedBranchId)?.name || ''} radius={[4, 4, 0, 0]}>
                                        {chartData.map((entry, idx) => (
                                            <Cell key={idx} fill={entry.isCurrent ? '#a8c8c4' : selectedBranchColor} />
                                        ))}
                                    </Bar>
                                ) : (
                                    branchIds.map((branchId, idx) => (
                                        <Bar
                                            key={branchId}
                                            dataKey={branchId}
                                            name={branches.find(b => b.id === branchId)?.name || branchId}
                                            stackId="a"
                                            fill={BRANCH_COLORS[idx % BRANCH_COLORS.length]}
                                            radius={idx === branchIds.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                        />
                                    ))
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                        {chartData.some(d => d.isCurrent) && (
                            <p className={styles.note}>* Mes actual: datos parciales</p>
                        )}
                    </div>
                </WidgetCard>
    )
}
